import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, Shield, Trash2, Crown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabels = {
  admin: { label: "Administrator", color: "bg-[#2563EB]/10 text-[#2563EB]", icon: Crown },
  manager: { label: "Manager", color: "bg-blue-100 text-blue-800", icon: Shield },
  creator: { label: "Content Creator", color: "bg-green-100 text-green-800", icon: Users },
  viewer: { label: "Betrachter", color: "bg-gray-100 text-gray-700", icon: Users },
};

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [roles, setRoles] = useState({});
  const [companies, setCompanies] = useState([]);
  const { t } = useLanguage();

  const load = async () => {
    try {
      const [t, r, c] = await Promise.all([
        axios.get(`${API}/team`, { withCredentials: true }),
        axios.get(`${API}/team/roles`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setTeam(t.data);
      setRoles(r.data);
      setCompanies(c.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const toggleCompany = async (user, companyId) => {
    const cur = user.company_ids || [];
    const next = cur.includes(companyId) ? cur.filter((x) => x !== companyId) : [...cur, companyId];
    try {
      await axios.put(`${API}/team/${user.user_id}/companies`, { company_ids: next }, { withCredentials: true });
      toast.success("Firmen-Zugriff aktualisiert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/team/${userId}/role`, { role }, { withCredentials: true });
      toast.success("Rolle aktualisiert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`${name} wirklich entfernen?`)) return;
    try {
      await axios.delete(`${API}/team/${userId}`, { withCredentials: true });
      toast.success("Benutzer entfernt");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div data-testid="team-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight">{t("team.title")}</h1>
        <p className="text-[var(--studio-text-muted)] mt-1">{t("team.subtitle")}</p>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(roleLabels).map(([key, { label, color }]) => (
          <Badge key={key} className={`${color} border-0 text-xs`}>{label}</Badge>
        ))}
      </div>

      {/* Team Members */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-lg">{t("team.members")} ({team.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {team.length === 0 ? (
            <p className="text-sm text-[var(--studio-text-muted)] text-center py-8">Keine Teammitglieder.</p>
          ) : (
            <div className="space-y-3">
              {team.map((user) => {
                const rl = roleLabels[user.role] || roleLabels.viewer;
                return (
                  <div key={user.user_id} className="p-4 rounded-lg bg-[var(--studio-bg)] space-y-3"
                    data-testid={`team-member-${user.user_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-[var(--studio-border)] text-sm font-medium">
                            {user.name?.[0] || user.email?.[0] || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{user.name || "Ohne Name"}</p>
                          <p className="text-xs text-[var(--studio-text-muted)]">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Select value={user.role || "admin"} onValueChange={(v) => updateRole(user.user_id, v)}>
                          <SelectTrigger className="w-44 h-8 text-xs" data-testid={`role-select-${user.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(roleLabels).map(([key, { label }]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => removeMember(user.user_id, user.name)}
                          data-testid={`remove-member-${user.user_id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {/* Firmen-Zugriff (Mandanten-Trennung) */}
                    <div className="flex flex-wrap items-center gap-1.5 pl-13" data-testid={`company-access-${user.user_id}`}>
                      <span className="text-[10px] uppercase tracking-wide text-[var(--studio-text-muted)] mr-1">Firmen-Zugriff:</span>
                      {user.role === "admin" ? (
                        <Badge className="bg-[#2563EB]/10 text-[#2563EB] border-0 text-[10px]">Alle Firmen (Admin)</Badge>
                      ) : (
                        companies.map((c) => {
                          const has = (user.company_ids || []).includes(c.id);
                          return (
                            <button key={c.id} type="button" onClick={() => toggleCompany(user, c.id)}
                              className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                                has ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                                    : "bg-white text-slate-400 border-slate-200 hover:border-slate-400"}`}
                              data-testid={`company-toggle-${user.user_id}-${c.id}`}>
                              {has ? "✓ " : ""}{c.name}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-[var(--studio-border)] shadow-none bg-[#0F0F0F] text-white">
        <CardContent className="p-5">
          <p className="font-heading font-semibold mb-2">{t("team.permissions")}</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-white/80 font-medium">{t("team.role_admin")}</p>
              <p className="text-white/50 text-xs">{t("team.admin_desc")}</p>
            </div>
            <div>
              <p className="text-white/80 font-medium">{t("team.role_manager")}</p>
              <p className="text-white/50 text-xs">{t("team.manager_desc")}</p>
            </div>
            <div>
              <p className="text-white/80 font-medium">{t("team.role_creator")}</p>
              <p className="text-white/50 text-xs">{t("team.creator_desc")}</p>
            </div>
            <div>
              <p className="text-white/80 font-medium">{t("team.role_viewer")}</p>
              <p className="text-white/50 text-xs">{t("team.viewer_desc")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
